export interface ThrottledPerfCountsOptions {
  /** Minimum interval between expensive count refreshes. Default 0 means refresh every call. */
  readonly intervalMs?: number;
  /** Time source for tests and non-browser runtimes. */
  readonly now?: () => number;
}

export type PerfCountsProvider = () => Record<string, number>;

const defaultNow = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

/**
 * Wrap an expensive count provider so perf overlays can stay enabled without traversing
 * large scene/display lists every frame.
 */
export function createThrottledPerfCounts(
  provider: PerfCountsProvider,
  opts: ThrottledPerfCountsOptions = {},
): PerfCountsProvider {
  const intervalMs = Math.max(0, opts.intervalMs ?? 0);
  const now = opts.now ?? defaultNow;
  let lastAt = Number.NEGATIVE_INFINITY;
  let cached: Record<string, number> = {};

  return () => {
    const t = now();
    if (t - lastAt >= intervalMs) {
      lastAt = t;
      cached = { ...provider() };
    }
    return { ...cached };
  };
}
